import { Namespace } from '../../../../types/namespace';
import { Flows, Folder } from '../../../../util/Types/Folder';
import { compareRanks } from '../dnd/rank';
import { isFluxbotFlow, isFormFlow } from '../utils';
import { FluxObject } from './context';

const byRank = <T extends { rank?: string | null }>(a: T, b: T): number =>
  compareRanks(a.rank ?? '', b.rank ?? '');

export interface LocalOrganizedItems {
  agents: Flows[];
  fluxbots: Flows[];
  objects: FluxObject[];
  vectorMemory: Namespace[];
}

type FlowWithRootFolder = { flow: Flows; rootFolderName: string };

export const stampFlow = (flow: Flows, folder: Folder): Flows => ({
  ...flow,
  folderId: flow.folderId ?? folder.id,
  spaceId: flow.spaceId ?? folder.space,
});

function extractAllFlowsFromFolder(
  folder: Folder,
  rootFolderName?: string,
): FlowWithRootFolder[] {
  const allFlows: FlowWithRootFolder[] = [];
  const currentRootName = rootFolderName || folder.name || '';

  if (folder.flows?.length) {
    folder.flows.forEach(flow => {
      allFlows.push({
        flow: stampFlow(flow, folder),
        rootFolderName: currentRootName,
      });
    });
  }
  if (folder.folders?.length) {
    folder.folders.forEach(subfolder => {
      allFlows.push(...extractAllFlowsFromFolder(subfolder, currentRootName));
    });
  }
  return allFlows;
}

function getAllFlowsFromAllFolders(folders: Folder[]): FlowWithRootFolder[] {
  return folders.flatMap(folder => extractAllFlowsFromFolder(folder));
}

export function organizeItemsByCategory(
  folders: Folder[],
  flows: Flows[],
  objects: FluxObject[],
  namespaces: Namespace[],
): LocalOrganizedItems {
  const result: LocalOrganizedItems = {
    agents: [],
    fluxbots: [],
    objects: objects || [],
    vectorMemory: namespaces || [],
  };
  const seenFlowIds = new Set<string>();

  const appendFlow = (flow: Flows, rootFolderName?: string) => {
    if (seenFlowIds.has(flow.id)) return;
    seenFlowIds.add(flow.id);
    if (isFluxbotFlow(flow, rootFolderName)) {
      result.fluxbots.push(flow);
      return;
    }
    if (isFormFlow(flow)) return;
    result.agents.push(flow);
  };

  getAllFlowsFromAllFolders(folders).forEach(({ flow, rootFolderName }) => {
    appendFlow(flow, rootFolderName);
  });
  (flows || []).forEach(flow => appendFlow(flow));

  return result;
}

export function filterItems(
  items: LocalOrganizedItems,
  searchTerm: string,
): LocalOrganizedItems {
  if (!searchTerm.trim()) return items;
  const searchLower = searchTerm.toLowerCase();
  return {
    agents: items.agents.filter(f =>
      f.name?.toLowerCase().includes(searchLower),
    ),
    fluxbots: items.fluxbots.filter(f =>
      f.name?.toLowerCase().includes(searchLower),
    ),
    objects: items.objects.filter(o =>
      o.name?.toLowerCase().includes(searchLower),
    ),
    vectorMemory: items.vectorMemory.filter(ns =>
      ns.name?.toLowerCase().includes(searchLower),
    ),
  };
}

const SKIP_FOLDER_NAMES = new Set([
  'agents',
  'chatbot',
  'data',
  'fluxbots',
  'shared',
  'vector memory',
]);

export function getAgentsFolderStructure(folders: Folder[]): {
  rootAgents: Flows[];
  rootForms: Flows[];
  subfolders: Folder[];
} {
  const rootAgents: Flows[] = [];
  const rootForms: Flows[] = [];
  const subfolders: Folder[] = [];
  const seenFlows = new Set<string>();
  const seenSubfolders = new Set<string>();

  function traverse(folderList: Folder[]) {
    folderList.forEach(folder => {
      const nameLower = folder.name?.trim().toLowerCase() || '';

      if (nameLower === 'agents') {
        (folder.flows || []).forEach(flow => {
          if (seenFlows.has(flow.id)) return;
          if (isFluxbotFlow(flow)) {
            return;
          }
          const stamped = stampFlow(flow, folder);
          if (isFormFlow(flow)) {
            seenFlows.add(flow.id);
            rootForms.push(stamped);
            return;
          }
          seenFlows.add(flow.id);
          rootAgents.push(stamped);
        });
        (folder.folders || []).forEach(subfolder => {
          const subName = subfolder.name?.trim().toLowerCase() || '';
          if (subName === 'vector memory') return;
          if (subfolder.section && subfolder.section !== 'agents') return;
          if (!seenSubfolders.has(subfolder.id)) {
            seenSubfolders.add(subfolder.id);
            subfolders.push(subfolder);
          }
        });
      } else if (!SKIP_FOLDER_NAMES.has(nameLower)) {
        if (folder.section && folder.section !== 'agents') return;
        if (!seenSubfolders.has(folder.id)) {
          seenSubfolders.add(folder.id);
          subfolders.push(folder);
        }
      }
    });
  }
  traverse(folders);
  return {
    rootAgents: [...rootAgents].sort(byRank),
    rootForms: [...rootForms].sort(byRank),
    subfolders: [...subfolders].sort(byRank),
  };
}

export function getSharedFolder(folderList: Folder[]): Folder | undefined {
  const atRoot = folderList.find(
    f => f.name?.trim().toLowerCase() === 'shared',
  );
  if (atRoot) return atRoot;
  for (const folder of folderList) {
    if (folder.folders?.length) {
      const found = getSharedFolder(folder.folders);
      if (found) return found;
    }
  }
  return undefined;
}

export function getSpaceSharedFolderOrPlaceholder(
  spaceId: string,
  spaceFolders: Folder[],
): Folder {
  const shared = getSharedFolder(spaceFolders);
  if (shared) return shared;
  return {
    id: `${spaceId}-Shared`,
    name: 'Shared',
    flows: [],
    folders: [],
    index: 0,
    flowsLength: 0,
    space: spaceId,
    section: 'agents',
  };
}

export function getSharedAgentsFromFolder(folder: Folder | undefined): Flows[] {
  if (!folder?.flows?.length) return [];
  return folder.flows.filter(f => !isFluxbotFlow(f) && !isFormFlow(f));
}
